/**
 * DiceTray.js — Arcane Rune Wheel Dice Roller
 *
 * A glowing magical circle with runes that spins to determine the roll result.
 * Pure CSS/SVG animation — no physics engine, no 3D.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './DiceTray.css';

const DIE_TYPES = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20'];
const DIE_LABELS = { d4: 'D4', d6: 'D6', d8: 'D8', d10: 'D10', d12: 'D12', d20: 'D20' };
const DIE_MAX = { d4: 4, d6: 6, d8: 8, d10: 10, d12: 12, d20: 20 };
const DIE_ICONS = { d4: '4', d6: '6', d8: '8', d10: '10', d12: '12', d20: '20' };

// Arcane rune characters
const RUNES = ['ᚠ','ᚢ','ᚦ','ᚨ','ᚱ','ᚲ','ᚷ','ᚹ','ᚺ','ᚾ','ᛁ','ᛃ','ᛇ','ᛈ','ᛉ','ᛊ','ᛏ','ᛒ','ᛖ','ᛗ','ᛚ','ᛜ','ᛞ','ᛟ'];

export default function DiceTray({ open, onToggle, pendingRequest, onRollSubmit, rollPurpose }) {
  const [selectedDie, setSelectedDie] = useState('d20');
  const [quantity, setQuantity] = useState(1);
  const [rolling, setRolling] = useState(false);
  const [results, setResults] = useState([]);       // [{type, value}, ...]
  const [total, setTotal] = useState(0);
  const [displayNumber, setDisplayNumber] = useState(null);   // Number shown on wheel during/after spin
  const [wheelPhase, setWheelPhase] = useState('idle');       // idle | spinning | landing | done
  const [currentRollIdx, setCurrentRollIdx] = useState(0);    // Which die in a multi-roll
  const spinTimerRef = useRef(null);
  const tickRef = useRef(null);

  const isRequested = pendingRequest && pendingRequest.sides;
  const requiredDie = isRequested ? `d${pendingRequest.sides}` : null;
  const requiredCount = isRequested ? (pendingRequest.count || 1) : 0;
  const bonus = isRequested ? (pendingRequest.bonus || 0) : 0;

  const activeDie = isRequested ? requiredDie : selectedDie;
  const activeCount = isRequested ? requiredCount : quantity;
  const maxVal = DIE_MAX[activeDie] || 20;

  // ─── Roll Logic ──────────────────────────────────────────────────────

  const rollOneDie = useCallback((dieType) => {
    return Math.floor(Math.random() * DIE_MAX[dieType]) + 1;
  }, []);

  const handleClear = useCallback(() => {
    setResults([]);
    setTotal(0);
    setDisplayNumber(null);
    setWheelPhase('idle');
    if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    if (tickRef.current) clearTimeout(tickRef.current);
    setRolling(false);
  }, []);

  const handleRoll = useCallback(() => {
    if (rolling) return;

    setRolling(true);
    setResults([]);
    setTotal(0);
    setCurrentRollIdx(0);
    setWheelPhase('spinning');

    // Roll all dice, then animate through them one at a time
    const allRolls = [];
    for (let i = 0; i < activeCount; i++) {
      allRolls.push({ type: activeDie, value: rollOneDie(activeDie) });
    }

    // Submit result IMMEDIATELY — backend starts processing while animation plays
    if (isRequested && onRollSubmit) {
      const rollValues = allRolls.map(r => r.value);
      const rollTotal = rollValues.reduce((s, v) => s + v, 0);
      onRollSubmit({
        dieType: requiredDie,
        count: requiredCount,
        bonus,
        values: rollValues,
        total: rollTotal + bonus,
        raw: `${requiredCount}${requiredDie}${bonus > 0 ? '+' + bonus : bonus < 0 ? bonus : ''}`,
      });
    }

    let idx = 0;

    const animateNextDie = () => {
      if (idx >= allRolls.length) {
        // All done
        setResults(allRolls);
        setTotal(allRolls.reduce((s, r) => s + r.value, 0));
        setWheelPhase('done');
        setRolling(false);

        // Animation done — close tray (result was already submitted at roll start)
        if (isRequested) {
          setTimeout(() => {
            handleClear();
            if (onToggle) onToggle();
          }, 2000);
        }
        return;
      }

      const target = allRolls[idx].value;
      const die = allRolls[idx].type;
      const dieMax = DIE_MAX[die];
      setCurrentRollIdx(idx);
      setWheelPhase('spinning');

      // Rapid number cycling — starts fast, slows down
      let tickCount = 0;
      const totalTicks = 20 + Math.floor(Math.random() * 10); // 20-30 ticks
      let currentDelay = 40; // Start at 40ms

      const tick = () => {
        tickCount++;
        if (tickCount < totalTicks) {
          // Random number while spinning
          setDisplayNumber(Math.floor(Math.random() * dieMax) + 1);
          currentDelay = 40 + (tickCount / totalTicks) * 200; // Decelerate: 40ms → 240ms
          tickRef.current = setTimeout(tick, currentDelay);
        } else {
          // Land on the result
          setDisplayNumber(target);
          setWheelPhase('landing');

          // Brief pause, then next die
          spinTimerRef.current = setTimeout(() => {
            idx++;
            animateNextDie();
          }, activeCount > 1 ? 800 : 1200);
        }
      };

      tick();
    };

    animateNextDie();
  }, [rolling, activeDie, activeCount, rollOneDie, isRequested, onRollSubmit, requiredDie, requiredCount, bonus, handleClear]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
      if (tickRef.current) clearTimeout(tickRef.current);
    };
  }, []);

  // Auto-roll when tray opens with a pending request
  const autoRollTriggered = useRef(false);
  const handleRollRef = useRef(handleRoll);
  handleRollRef.current = handleRoll;
  useEffect(() => {
    if (open && isRequested && !autoRollTriggered.current) {
      autoRollTriggered.current = true;
      const t = setTimeout(() => handleRollRef.current(), 500);
      return () => clearTimeout(t);
    }
    if (!open) autoRollTriggered.current = false;
  }, [open, isRequested]);

  const handleSubmitRoll = useCallback(() => {
    if (!isRequested || results.length === 0 || !onRollSubmit) return;

    const validResults = results.filter(r => r.type === requiredDie);
    if (validResults.length < requiredCount) return;

    const rollValues = validResults.slice(0, requiredCount).map(r => r.value);
    const rollTotal = rollValues.reduce((s, v) => s + v, 0);

    onRollSubmit({
      dieType: requiredDie,
      count: requiredCount,
      bonus,
      values: rollValues,
      total: rollTotal + bonus,
      raw: `${requiredCount}${requiredDie}${bonus > 0 ? '+' + bonus : bonus < 0 ? bonus : ''}`,
    });

    handleClear();
  }, [isRequested, results, requiredDie, requiredCount, bonus, onRollSubmit, handleClear]);

  // ─── Build rune positions around the wheel ───────────────────────────

  const runeCount = maxVal;
  const runeElements = [];
  for (let i = 0; i < runeCount; i++) {
    const angle = (i / runeCount) * 360;
    runeElements.push(
      <div
        key={i}
        className="rune-symbol"
        style={{
          transform: `rotate(${angle}deg) translateY(-42%) rotate(-${angle}deg)`,
        }}
      >
        {RUNES[i % RUNES.length]}
      </div>
    );
  }

  // Number ring — actual die numbers around the outer edge
  const numberElements = [];
  for (let i = 1; i <= maxVal; i++) {
    const angle = ((i - 1) / maxVal) * 360;
    numberElements.push(
      <div
        key={i}
        className={`wheel-number ${displayNumber === i && wheelPhase === 'landing' ? 'wheel-number-hit' : ''}`}
        style={{
          transform: `rotate(${angle}deg) translateY(-47%) rotate(-${angle}deg)`,
        }}
      >
        {i}
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <>
      {open && <div className="dice-tray-backdrop" onClick={onToggle} />}

      <div className={`dice-tray-panel ${open ? 'dice-tray-panel-open' : ''}`}>
        {/* Header */}
        <div className="dice-tray-header">
          <h3 className="dice-tray-title">Arcane Roller</h3>

          {isRequested && (
            <div className="dice-tray-request-banner">
              <div className="dice-roll-description">
                {pendingRequest.description || `Roll ${DIE_LABELS[requiredDie]}`}
              </div>
            </div>
          )}

          <div className="dice-tray-selector">
            {DIE_TYPES.map(die => {
              const locked = isRequested && die !== requiredDie;
              return (
                <button
                  key={die}
                  className={`dice-btn ${selectedDie === die ? 'dice-btn-active' : ''} ${locked ? 'dice-btn-locked' : ''}`}
                  onClick={() => !locked && setSelectedDie(die)}
                  disabled={locked}
                >
                  <span className="dice-btn-icon">{DIE_ICONS[die]}</span>
                  <span className="dice-btn-label">{DIE_LABELS[die]}</span>
                </button>
              );
            })}

            {!isRequested && (
              <div className="dice-quantity">
                <button className="dice-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
                <span className="dice-qty-value">{quantity}</span>
                <button className="dice-qty-btn" onClick={() => setQuantity(q => Math.min(10, q + 1))}>+</button>
              </div>
            )}

            {!isRequested && (
              <>
                <button className="dice-throw-btn" onClick={handleRoll} disabled={rolling}>
                  {rolling ? 'Casting...' : 'Cast'}
                </button>
                <button className="dice-clear-btn" onClick={handleClear}>
                  Clear
                </button>
              </>
            )}
          </div>
        </div>

        {/* Rune Wheel */}
        <div className="rune-wheel-area">
          <div className={`rune-wheel ${wheelPhase === 'spinning' ? 'rune-wheel-spinning' : ''} ${wheelPhase === 'landing' ? 'rune-wheel-landing' : ''}`}>
            {/* Outer glow ring */}
            <div className="wheel-glow-ring" />

            {/* Outer circle with numbers */}
            <div className="wheel-numbers-ring">
              {numberElements}
            </div>

            {/* Inner rune ring */}
            <div className="wheel-rune-ring">
              {runeElements}
            </div>

            {/* Center result display */}
            <div className={`wheel-center ${wheelPhase === 'landing' ? 'wheel-center-flash' : ''}`}>
              <div className="wheel-die-label">{DIE_LABELS[activeDie]}</div>
              <div className={`wheel-result-number ${wheelPhase === 'spinning' ? 'wheel-result-cycling' : ''} ${wheelPhase === 'landing' ? 'wheel-result-landed' : ''}`}>
                {displayNumber !== null ? displayNumber : '?'}
              </div>
              {activeCount > 1 && rolling && (
                <div className="wheel-roll-counter">
                  Die {currentRollIdx + 1} of {activeCount}
                </div>
              )}
            </div>

            {/* Arcane circle decorative lines */}
            <svg className="wheel-arcane-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="140" className="arcane-circle-outer" />
              <circle cx="150" cy="150" r="120" className="arcane-circle-inner" />
              <circle cx="150" cy="150" r="60" className="arcane-circle-center" />
              {/* Cross lines */}
              {[0, 45, 90, 135].map(angle => (
                <line
                  key={angle}
                  x1={150 + Math.cos(angle * Math.PI / 180) * 60}
                  y1={150 + Math.sin(angle * Math.PI / 180) * 60}
                  x2={150 + Math.cos(angle * Math.PI / 180) * 120}
                  y2={150 + Math.sin(angle * Math.PI / 180) * 120}
                  className="arcane-line"
                />
              ))}
              {[0, 45, 90, 135].map(angle => (
                <line
                  key={`b${angle}`}
                  x1={150 - Math.cos(angle * Math.PI / 180) * 60}
                  y1={150 - Math.sin(angle * Math.PI / 180) * 60}
                  x2={150 - Math.cos(angle * Math.PI / 180) * 120}
                  y2={150 - Math.sin(angle * Math.PI / 180) * 120}
                  className="arcane-line"
                />
              ))}
            </svg>
          </div>
        </div>

        {/* Results bar */}
        <div className="dice-tray-results">
          {results.length > 0 ? (
            <>
              <div className="dice-results-list">
                {results.map((r, i) => (
                  <span key={i} className="dice-result-chip">
                    {DIE_LABELS[r.type]}: {r.value}
                  </span>
                ))}
              </div>
              <div className="dice-results-total">
                Total: {total}
                {isRequested && bonus !== 0 && (
                  <span className="dice-results-bonus">
                    {' '}+ {bonus} = {total + bonus}
                  </span>
                )}
              </div>
              {isRequested && results.length > 0 && (
                <div className="dice-autosubmit-notice">Submitting...</div>
              )}
            </>
          ) : (
            <div className="dice-results-empty">
              {isRequested
                ? `Cast the runes for a ${DIE_LABELS[requiredDie]}!`
                : 'Choose your die and cast the runes'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
